// When phones go down (TAS-466). The research for the final scoreboard could
// not tell how long players kept looking: the website sends "Page Hidden"
// (apps/web/src/lib/funnelEvents.js) and the game sent nothing when a screen
// locked or the player switched apps.
//
//   Page Hidden    { route, seconds_visible, sessionCode? }  the tab went away
//   Page Visible   { route, seconds_hidden,  sessionCode? }  it came back
//
// seconds_visible is this stretch in front: since the page loaded (or since a
// page opened in a background tab was first looked at), or since the player
// last came back. Each event reports only its own stretch, so the sum of a
// phone's seconds_visible is its time in front. Property names follow the
// website's where they mean the same thing.
//
// sessionCode comes from ?code= in the URL when there is one. track() already
// carries the session's code once the game has set it; the URL covers the
// pages that run before that (and outlives it after the game).
//
// One event per transition: iOS fires visibilitychange and pagehide together
// on a close, and nothing is sent for the state the page loaded in. Separate
// from realtime.js and GameFlowProvider's own visibility handlers, which
// reconnect; this only reports.
import { track } from '@tasted/utils/analytics';

// Sent as the page goes away: skips the batch queue and hands the request to
// the browser, which delivers it after the page is gone (as the website does).
export const BEACON = { transport: 'sendBeacon', send_immediately: true };

const now = () => (typeof performance !== 'undefined' ? performance.now() : Date.now());
const seconds = (ms) => Math.round(Math.max(0, ms) / 100) / 10;

// Numbers each transition across the page load, so track()'s 5 s dedupe never
// takes a quick second hide for a repeat of the first.
let seq = 0;

function routeProps() {
  const props = { route: window.location.pathname };
  try {
    const code = new URLSearchParams(window.location.search).get('code');
    if (code) props.sessionCode = code;
  } catch { /* no URL to read */ }
  return props;
}

let stopRunning = null;

/**
 * Start reporting hides and returns. Call once, after analytics is set up
 * (main.jsx). Returns a cleanup function; a second call while running returns
 * the first call's cleanup and adds no listeners.
 */
export function startPageVisibilityTracking() {
  if (stopRunning) return stopRunning;
  if (typeof document === 'undefined') return () => {};

  let hidden = document.visibilityState === 'hidden';
  // Null while hidden: a page opened in a background tab starts counting when
  // the player first looks at it.
  let shownAt = hidden ? null : now();
  let hiddenAt = hidden ? now() : null;
  // A page opened in a background tab has no hide to report a return from.
  let sawHide = false;

  const onHide = () => {
    if (hidden) return;
    hidden = true;
    sawHide = true;
    hiddenAt = now();
    const visibleMs = shownAt === null ? 0 : hiddenAt - shownAt;
    shownAt = null;
    seq += 1;
    try {
      track('Page Hidden', { ...routeProps(), seconds_visible: seconds(visibleMs), dedupeKey: `hide-${seq}` }, BEACON);
    } catch { /* analytics must never break the page */ }
  };
  const onShow = () => {
    if (!hidden) return;
    hidden = false;
    shownAt = now();
    if (!sawHide) return;
    const hiddenMs = hiddenAt === null ? 0 : shownAt - hiddenAt;
    hiddenAt = null;
    seq += 1;
    try {
      track('Page Visible', { ...routeProps(), seconds_hidden: seconds(hiddenMs), dedupeKey: `show-${seq}` });
    } catch { /* analytics must never break the page */ }
  };
  const onVisibility = () => {
    if (document.visibilityState === 'hidden') onHide();
    else onShow();
  };

  document.addEventListener('visibilitychange', onVisibility);
  window.addEventListener('pagehide', onHide);
  stopRunning = () => {
    document.removeEventListener('visibilitychange', onVisibility);
    window.removeEventListener('pagehide', onHide);
    stopRunning = null;
  };
  return stopRunning;
}

export default startPageVisibilityTracking;
